import { POOR_ACCURACY_M, findContinuableVisit } from '@recipes/domain';
import { useQuery } from '@tanstack/react-query';
import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { offlineDb } from '@/lib/offline/db';
import { PositionError, getPlatform, type DevicePosition } from '@/lib/platform';
import { queryKeys } from '@/lib/query-keys';

export type PositionState =
  | { status: 'idle' | 'locating' }
  | { status: 'ready'; position: DevicePosition; poorAccuracy: boolean }
  | { status: 'error'; reason: PositionError['reason'] };

/**
 * Reads the device position exactly once per mount (never watches). A denied permission is
 * remembered for the session so that the prompt does not come back while capturing.
 */
const SESSION_DENIED = 'rm.tasting.locationDenied';

export function useDevicePosition(enabled: boolean) {
  const [state, setState] = useState<PositionState>({ status: 'idle' });
  const requested = useRef(false);

  const locate = useCallback(async () => {
    setState({ status: 'locating' });
    try {
      const platform = await getPlatform();
      const position = await platform.getCurrentPosition();
      setState({
        status: 'ready',
        position,
        poorAccuracy: position.accuracyM !== null && position.accuracyM > POOR_ACCURACY_M,
      });
    } catch (error) {
      const reason = error instanceof PositionError ? error.reason : 'unavailable';
      if (reason === 'denied') {
        try {
          sessionStorage.setItem(SESSION_DENIED, '1');
        } catch {
          /* private mode */
        }
      }
      setState({ status: 'error', reason });
    }
  }, []);

  useEffect(() => {
    if (!enabled || requested.current) return;
    requested.current = true;
    let deniedBefore = false;
    try {
      deniedBefore = sessionStorage.getItem(SESSION_DENIED) === '1';
    } catch {
      /* private mode */
    }
    if (deniedBefore) setState({ status: 'error', reason: 'denied' });
    else void locate();
  }, [enabled, locate]);

  return { state, retry: locate };
}

/** Visits captured offline that still wait for synchronization. */
export function usePendingVisits() {
  return (
    useLiveQuery(() => offlineDb.pendingVisits.orderBy('createdAt').reverse().toArray(), []) ?? []
  );
}

/**
 * The visit a new dish would continue: one of the user's own visits that started less than
 * 4 hours ago within 200 m (docs/finished/tasting-notes-spec.md 5.2).
 */
export function useContinuableVisit(position: { lat: number; lng: number } | null, enabled = true) {
  // Rounded to the hour so the query key stays stable while the form is open.
  const from = useMemo(() => {
    const d = new Date(Date.now() - 4 * 3600_000);
    d.setMinutes(0, 0, 0);
    return d.toISOString();
  }, []);
  const recent = useQuery({
    queryKey: queryKeys.tasting.visits({ scope: 'mine', from }),
    queryFn: () => api.visits.list({ scope: 'mine', from, limit: 10 }),
    enabled,
    staleTime: 30_000,
  });
  return useMemo(() => {
    const visits = recent.data?.items ?? [];
    const match = findContinuableVisit(
      visits.map((v) => ({
        id: v.id,
        visitedAt: v.visitedAt,
        lat: v.lat,
        lng: v.lng,
        place: v.place ? { lat: v.place.lat, lng: v.place.lng } : null,
      })),
      new Date(),
      position,
    );
    return match ? (visits.find((v) => v.id === match.id) ?? null) : null;
  }, [recent.data, position]);
}
